import React from 'react'

/**
 * CooldownElapsedPrompt — fires once per cooldown cycle, the first time a
 * student lands on /subjects (or /profile) after their free-tier subject
 * swap cooldown elapses (subjectGate.can_swap_now flips true).
 *
 * Two paths:
 *   - "Manter as mesmas"   → dismiss, persist localStorage flag so the
 *                            prompt doesn't re-fire on subsequent visits
 *                            within this same cooldown cycle.
 *   - "Trocar disciplinas" → open the existing FreeSubjectPickerModal in
 *                            swap mode, dismiss localStorage same way.
 *
 * Per-cycle dismissal is keyed on `pickedAt` (i.e. free_subject_codes_
 * updated_at). When the student actually swaps, the timestamp moves
 * forward and the key changes, so the next cooldown end will surface
 * the prompt again. Pure additions (which don't move the timer) keep
 * the key stable — the student won't be re-prompted mid-cooldown just
 * because they added a subject.
 */

interface Props {
  /** Currently-picked subject codes — shown in the body for context. */
  pickedCodes:    string[]
  /** Optional name lookup so we render names instead of bare codes. */
  subjectNameByCode?: Record<string, string>
  /** Cooldown window length (admin-configured) — surfaced in copy. */
  cooldownDays:   number
  /** Confirms keep — parent persists the localStorage key. */
  onKeep:         () => void
  /** Opens the swap picker — parent persists the localStorage key and
   *  triggers the picker. */
  onSwap:         () => void
}

export const CooldownElapsedPrompt: React.FC<Props> = ({
  pickedCodes, subjectNameByCode, cooldownDays, onKeep, onSwap,
}) => {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Período de troca terminou"
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">
            O teu período de troca chegou ao fim
          </h2>
          <p className="text-sm text-gray-600 mt-1 leading-relaxed">
            Já podes trocar as disciplinas em foco se quiseres mudar. Senão, mantém as actuais
            {cooldownDays > 0 && <> e voltas a poder trocar daqui a {cooldownDays} dias.</>}
          </p>
        </div>

        {pickedCodes.length > 0 && (
          <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Actualmente em foco
            </p>
            <div className="flex flex-wrap gap-1.5">
              {pickedCodes.map(code => (
                <span
                  key={code}
                  className="text-xs px-3 py-1 rounded-full bg-white border border-gray-200 text-gray-700"
                >
                  {subjectNameByCode?.[code] ?? code}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="px-5 py-4 space-y-2">
          <button
            onClick={onSwap}
            className="w-full bg-indigo-600 text-white text-sm font-semibold py-3 rounded-xl hover:bg-indigo-700 transition-colors"
          >
            Trocar disciplinas
          </button>
          <button
            onClick={onKeep}
            className="w-full bg-white border border-gray-200 text-gray-700 text-sm font-semibold py-3 rounded-xl hover:bg-gray-50 transition-colors"
          >
            Manter as mesmas
          </button>
        </div>
      </div>
    </div>
  )
}
